import {
  Button,
  type ButtonProps,
  Flex,
  SimpleGrid,
  Text,
  TextInput,
  type TextInputProps,
} from '@mantine/core';
import { useVerifyCompanyNcc } from '../../companyApi';
import { showNotification } from '@shared/utils';
import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  NccVerifiedStatusIcon,
  type NccVerifiedStatus,
} from '../NccVerifiedStatusIcon';
import { CompanyNccConfirmButton } from './company-ncc-confirm-button';
import classes from './company-ncc-form-field.module.scss';

type CompanyNccFormFieldProps = Omit<TextInputProps, 'description'> & {
  title?: string;
  description?: string;
  nccVerified?: NccVerifiedStatus;
  onNccVerifiedChange?: (value: boolean | null) => void;
  /** Enables manual confirmation of the saved NCC for an existing company. */
  companyId?: string;
  isNccChanged?: boolean;
};

export const CompanyNccFormField = ({
  title = 'NCC',
  description = 'Введите регистрационный номер компании',
  placeholder = '0000000Р',
  nccVerified: nccVerifiedProp,
  onNccVerifiedChange,
  companyId,
  isNccChanged = false,
  value,
  onChange,
  error,
  ...props
}: CompanyNccFormFieldProps) => {
  const [nccVerified, setNccVerified] =
    useState<NccVerifiedStatus>(nccVerifiedProp);
  const { mutate: verifyNcc, isPending: isVerifying } = useVerifyCompanyNcc();

  useEffect(() => {
    setNccVerified(nccVerifiedProp);
  }, [nccVerifiedProp]);

  const handleNccVerifiedChange = (verified: boolean | null) => {
    setNccVerified(verified);
    onNccVerifiedChange?.(verified);
  };

  const handleVerify = () => {
    const ncc = typeof value === 'string' ? value.trim() : '';

    if (!ncc) {
      showNotification({
        variant: 'error',
        message: 'NCC обязателен для заполнения',
      });
      return;
    }

    verifyNcc(
      { ncc },
      {
        onSuccess: data => handleNccVerifiedChange(data.nccVerified),
        onError: verifyError => {
          const message = isAxiosError<{ message?: string }>(verifyError)
            ? verifyError.response?.data?.message
            : undefined;

          showNotification({
            error: verifyError,
            variant: 'error',
            message: message || 'Не удалось проверить NCC',
          });
        },
      }
    );
  };

  const nccInput = (
    <TextInput
      leftSection={<NccVerifiedStatusIcon nccVerified={nccVerified} />}
      variant='filled'
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      error={error}
      w='100%'
      flex={1}
      {...props}
    />
  );

  const renderVerifyButton = (buttonProps: ButtonProps) => (
    <Button
      variant='secondary'
      size='md'
      onClick={handleVerify}
      loading={isVerifying}
      disabled={isVerifying}
      {...buttonProps}
    >
      Проверить
    </Button>
  );

  return (
    <Flex gap='24' className={classes.formRow}>
      <Flex direction='column' gap={6} className={classes.leftColumn}>
        <Text className={classes.title}>{title}</Text>
        <Text className={classes.description}>{description}</Text>
      </Flex>
      <Flex direction='column' gap={8} className={classes.rightColumn}>
        {companyId ? (
          // With manual confirmation the buttons share a row under the input.
          <>
            {nccInput}
            <SimpleGrid cols={2} spacing={8}>
              {renderVerifyButton({ fullWidth: true })}
              <CompanyNccConfirmButton
                companyId={companyId}
                isConfirmed={nccVerifiedProp === true}
                isNccChanged={isNccChanged}
                onConfirm={handleNccVerifiedChange}
              />
            </SimpleGrid>
          </>
        ) : (
          <Flex align='center' gap={8}>
            {nccInput}
            {renderVerifyButton({ miw: 120 })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
